import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Sparkles, Clock } from 'lucide-react';

/**
 * "Mira saved you X minutes this week" strip for the CEO/Leader dashboard.
 * Creates a visible habit-loop between using Mira and seeing payoff.
 *
 * Data source: GET /api/operator-mira/stats — `minutes_saved_week` is
 * computed from per-intent average UI-form-fill times multiplied by the
 * number of non-undone writes in the last 7 days.
 */
export default function OperatorMiraTimeSavedStrip() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/operator-mira/stats').then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  if (!stats || stats.week_writes === 0) return null;

  const mins = stats.minutes_saved_week;
  const hrs = stats.hours_saved_week;
  const display = hrs >= 1 ? `${hrs} hr${hrs === 1 ? '' : 's'}` : `${mins} min${mins === 1 ? '' : 's'}`;

  // Top intent for the nudge line
  const sortedIntents = Object.entries(stats.week_by_intent || {}).sort((a, b) => b[1] - a[1]);
  const topIntent = sortedIntents[0]?.[0]?.replace('add_', '').replace('_', ' ') || 'writes';

  return (
    <div
      data-testid="mira-time-saved-strip"
      className="rounded-lg border px-4 py-3 flex items-center justify-between"
      style={{ borderColor: '#F5C86B', background: 'linear-gradient(90deg, #FFF8E7 0%, #FEFAF0 100%)' }}
    >
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1B2A4A' }}>
          <Sparkles size={14} style={{ color: '#F5C86B' }} />
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>
            Operator Mira saved you <span style={{ color: '#B45309' }}>{display}</span> this week
          </p>
          <p className="text-[11px]" style={{ color: '#6B7280' }}>
            {stats.week_writes} write{stats.week_writes === 1 ? '' : 's'} · Most-used: <span className="font-medium" style={{ color: '#1B2A4A' }}>{topIntent}</span> · {stats.capability_matrix_size} capabilities available
          </p>
        </div>
      </div>
      <div className="text-right hidden sm:block">
        <div className="flex items-center gap-1 justify-end">
          <Clock size={10} style={{ color: '#6B7280' }} />
          <p className="text-[10px] uppercase tracking-wide" style={{ color: '#6B7280' }}>Today</p>
        </div>
        <p className="text-base font-bold" style={{ color: '#1B2A4A' }}>{stats.today_writes} <span className="text-[10px] font-normal" style={{ color: '#9CA3AF' }}>/ {stats.daily_cap}</span></p>
      </div>
    </div>
  );
}
